import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys, CACHE_TIERS } from '@/lib/queryClient';

export interface WithdrawalAddress {
  id: string;
  user_id: string;
  address: string;
  label: string | null;
  use_count: number;
  last_used_at: string;
  created_at: string;
}

export function useWithdrawalAddresses(walletAddress: string | undefined) {
  const queryClient = useQueryClient();

  const { data: addresses = [], isLoading } = useQuery({
    queryKey: queryKeys.wallet.withdrawalAddresses(walletAddress!),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('withdrawal_addresses')
        .select('*')
        .eq('user_id', walletAddress!)
        .order('use_count', { ascending: false })
        .order('last_used_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data as WithdrawalAddress[];
    },
    enabled: !!walletAddress,
    ...CACHE_TIERS.TIER_3,
  });

  const recordAddress = useMutation({
    mutationFn: async ({ address, label }: { address: string; label?: string }) => {
      if (!walletAddress) throw new Error('Wallet not connected');

      // Check if address already exists for this user
      const { data: existing } = await supabase
        .from('withdrawal_addresses')
        .select('use_count')
        .eq('user_id', walletAddress)
        .eq('address', address)
        .maybeSingle();

      const newCount = (existing?.use_count ?? 0) + 1;

      const upsertPayload: Record<string, unknown> = {
        user_id: walletAddress,
        address,
        use_count: newCount,
        last_used_at: new Date().toISOString(),
      };
      // Only include label when explicitly provided to avoid overwriting existing labels
      if (label !== undefined) {
        upsertPayload.label = label || null;
      }

      const { error } = await supabase
        .from('withdrawal_addresses')
        .upsert(upsertPayload, { onConflict: 'user_id,address' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.wallet.withdrawalAddresses(walletAddress!),
      });
    },
  });

  const updateLabel = useMutation({
    mutationFn: async ({ id, label }: { id: string; label: string }) => {
      const { error } = await supabase
        .from('withdrawal_addresses')
        .update({ label: label || null })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.wallet.withdrawalAddresses(walletAddress!),
      });
    },
  });

  const clearHistory = useMutation({
    mutationFn: async () => {
      if (!walletAddress) throw new Error('Wallet not connected');

      const { error } = await supabase
        .from('withdrawal_addresses')
        .delete()
        .eq('user_id', walletAddress);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.wallet.withdrawalAddresses(walletAddress!),
      });
    },
  });

  return { addresses, isLoading, recordAddress, updateLabel, clearHistory };
}
